import { type ReactNode, useState } from 'react';

import { ActionIconButton, DataViewActionCell } from '../components/DataViewActionCell';
import { DataTable, type TableColumn } from '../components/DataTable';
import { EntityManageDialog, EntityViewDialog } from '../components/EntityDialogs';
import { PageHeader } from '../components/PageHeader';
import { Button, Card } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedResource } from '../hooks/usePaginatedResource';
import { useT, userError } from '../i18n/messages';
import type { PaginatedResponse } from '../lib/api';

interface ResourceDetailConfig<T> {
  action: 'manage' | 'view';
  actionLabel: string;
  getTitle: (row: T) => string;
  load?: (token: string, id: string) => Promise<unknown>;
  render?: (data: unknown, row: T) => ReactNode;
  size?: 'medium' | 'large';
}

function defaultDetailRenderer(data: unknown) {
  return <pre className="detail-json">{JSON.stringify(data, null, 2)}</pre>;
}

export function ResourcePage<T extends { id?: string }>({
  titleKey,
  testId,
  load,
  columns,
  detail,
}: {
  titleKey: string;
  testId: string;
  load: (token: string, page: number, signal?: AbortSignal) => Promise<PaginatedResponse<T>>;
  columns: TableColumn<T>[];
  detail?: ResourceDetailConfig<T>;
}) {
  const t = useT();
  const { tokens } = useAuth();
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);
  const [detailState, setDetailState] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    data: unknown;
    error: string | null;
  }>({ status: 'idle', data: null, error: null });
  const resource = usePaginatedResource(tokens?.access_token ?? '', page, load);

  async function openDetail(row: T) {
    if (!detail) {
      return;
    }

    setSelectedRow(row);

    if (!detail.load || !tokens?.access_token || !row.id) {
      setDetailState({ status: 'success', data: row, error: null });
      return;
    }

    setDetailState({ status: 'loading', data: null, error: null });

    try {
      const data = await detail.load(tokens.access_token, row.id);
      setDetailState({ status: 'success', data, error: null });
    } catch (error) {
      setDetailState({
        status: 'error',
        data: null,
        error: userError(error, t),
      });
    }
  }

  function closeDetail() {
    setSelectedRow(null);
    setDetailState({ status: 'idle', data: null, error: null });
  }

  const resolvedColumns = detail
    ? [
        ...columns,
        {
          key: 'actions',
          label: 'billing.column.actions',
          className: 'table-actions-cell',
          truncate: false,
          render: (row: T) => (
            <DataViewActionCell>
              <ActionIconButton
                icon={detail.action}
                label={detail.actionLabel}
                onClick={() => void openDetail(row)}
              />
            </DataViewActionCell>
          ),
        } satisfies TableColumn<T>,
      ]
    : columns;

  const DetailDialog = detail?.action === 'manage' ? EntityManageDialog : EntityViewDialog;

  if (!tokens) {
    return <section className="panel page-panel">{t('billing.error.unauthorized')}</section>;
  }

  return (
    <section className="page" data-testid={testId}>
      <PageHeader title={t(titleKey)} />
      {resource.status === 'loading' ? (
        <section className="panel page-panel">{t('billing.resource.loading')}</section>
      ) : null}
      {resource.status === 'error' ? (
        <Card className="panel panel-error page-panel">
          <p>{userError(resource.error, t)}</p>
          <Button onClick={resource.reload} type="button">
            {t('billing.resource.retry')}
          </Button>
        </Card>
      ) : null}
      {resource.status === 'success' && resource.data ? (
        <>
          {resource.data.items.length === 0 ? (
            <section className="panel page-panel">{t('billing.resource.empty')}</section>
          ) : (
            <DataTable columns={resolvedColumns} rows={resource.data.items} />
          )}
          <footer className="pagination">
            <Button
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              type="button"
              variant="secondary"
            >
              {t('billing.resource.previous')}
            </Button>
            <span className="pagination-summary">
              {t('billing.pagination.summary', {
                page: resource.data.pagination.page,
                total: Math.max(resource.data.pagination.total_pages, 1),
              })}
            </span>
            <Button
              disabled={
                resource.data.pagination.total_pages === 0 ||
                page >= resource.data.pagination.total_pages
              }
              onClick={() => setPage((current) => current + 1)}
              type="button"
              variant="secondary"
            >
              {t('billing.resource.next')}
            </Button>
          </footer>
        </>
      ) : null}
      {detail && selectedRow ? (
        <DetailDialog
          onClose={closeDetail}
          open={Boolean(selectedRow)}
          size={detail.size}
          title={detail.getTitle(selectedRow)}
        >
          {detailState.status === 'loading' ? (
            <section className="panel">{t('billing.resource.loading')}</section>
          ) : null}
          {detailState.status === 'error' ? (
            <section className="panel panel-error">
              {detailState.error ?? t('billing.resource.error')}
            </section>
          ) : null}
          {detailState.status === 'success'
            ? (detail.render ?? defaultDetailRenderer)(detailState.data ?? selectedRow, selectedRow)
            : null}
        </DetailDialog>
      ) : null}
    </section>
  );
}
